import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshToken, isTokenExpired } from '../services/authService';
import { API_CONFIG } from '../config/env';

let isRefreshing = false;
let interceptorsInitialized = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const getRequestPath = (url?: string): string => {
  if (!url) {
    return '';
  }

  try {
    return new URL(url, window.location.origin).pathname.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
};

const shouldBypassRefreshHandling = (url?: string): boolean => {
  const path = getRequestPath(url);
  const authEndpoints = [
    API_CONFIG.AUTH_LOGIN_ENDPOINT,
    API_CONFIG.AUTH_REGISTER_ENDPOINT,
    API_CONFIG.AUTH_REFRESH_ENDPOINT,
    API_CONFIG.AUTH_FORGOT_PASSWORD_ENDPOINT,
    API_CONFIG.AUTH_RESET_PASSWORD_ENDPOINT,
  ].map((endpoint) => endpoint.toLowerCase());

  return authEndpoints.some((endpoint) => path.endsWith(endpoint));
};

export const setupAxiosInterceptors = () => {
  if (interceptorsInitialized) {
    return;
  }

  interceptorsInitialized = true;

  // Request interceptor to add auth token
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('accessToken');
      if (token && !isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry && !shouldBypassRefreshHandling(originalRequest.url)) {
        if (isRefreshing) {
          // If already refreshing, queue the request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axios(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshTokenValue = localStorage.getItem('refreshToken');
        const username = localStorage.getItem('username');

        if (!refreshTokenValue || !username) {
          // No refresh token or username, redirect to login
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        try {
          const response = await refreshToken({
            token: refreshTokenValue,
            name: username
          });

          // Update tokens in localStorage
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('accessTokenExpiredAt', response.accessTokenExpiredAt);
          localStorage.setItem('refreshTokenExpiredAt', response.refreshTokenExpiredAt);

          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
          }

          processQueue(null, response.accessToken);

          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
